import { CampaignAgentPhones } from 'components/views/campaign/agent-phones';
import { CampaignCallCenter } from 'components/views/campaign/call-center';
import { CampaignDnc } from 'components/views/campaign/dnc';
import { CampaignLeadList } from 'components/views/campaign/lead-list';
import { CampaignLeadLists } from 'components/views/campaign/lead-lists';
import { CampaignLeadSource } from 'components/views/campaign/lead-source';
import { CampaignOutreachTemplate } from 'components/views/campaign/outreach-template/outreach-template';
import { CampaignPosting } from 'components/views/campaign/posting';
import { CampaignTestLeads } from 'components/views/campaign/test-leads';
import { Vendors } from 'components/views/campaign/vendors/vendors';
import { ConversationPage } from 'components/views/conversation/conversation';
import { Login } from 'components/views/login/login';
import { PageLoading } from 'components/views/page-loading';
import LeadListCreateViewWrapper from 'lead-list/views/LeadListCreate/LeadListCreateViewWrapper';
import { CURRENT_AVAILABLE_GROUPS, useAuthContext, useHasAuthGroup } from 'providers/auth';
import { FC, lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { useOriginalLocation } from 'utils/location.hook';
import LoginCallback from './components/views/login-callback';
import routes from './routes';

const LeadListDashboard = lazy(() => import('./lead-list/views/Dashboard/Dashboard'));
const LeadListDetails = lazy(() => import('./lead-list/views/LeadListDetails/LeadListDetails'));
const LeadListsView = lazy(() => import('./lead-list/views/LeadLists/LeadLists'));

const CompaniesList = lazy(() => import('./components/views/companies'));
const Company = lazy(() => import('./components/views/company'));
const CompanyBrands = lazy(() => import('./components/views/company/brands'));
const CompanyCallCenters = lazy(() => import('./components/views/company/call-centers'));
const CompanyCampaigns = lazy(() => import('./components/views/company/campaigns'));
const CompanyDetails = lazy(() => import('./components/views/company/details'));
const CompanyNlpAgents = lazy(() => import('./components/views/company/nlp-agents'));
const CompanyOutreachTemplates = lazy(
  () => import('./components/views/company/outreach-templates'),
);
const CompanyOutreachTemplate = lazy(() => import('components/views/company/outreach-template'));
const CompanyScripts = lazy(() => import('./components/views/company/scripts'));
const CompanyVirtualAgents = lazy(() => import('./components/views/company/virtual-agents'));
const UserCompanies = lazy(
  () => import('./components/views/company/user-companies/user-companies'),
);
const CompanyIvrs = lazy(() => import('./components/views/company/ivrs/ivrs'));
const ExternalIvrs = lazy(() => import('components/views/company/ivrs/external-ivrs'));
const CompanyUserPoolPage = lazy(() => import('components/views/company/user-pools/user-pools'));
const CompanySettingsPage = lazy(() => import('components/views/company/settings/settings'));
const CompanySettingsOrganization = lazy(
  () => import('components/views/company/settings/organization'),
);
const CompanySettingsWebhooks = lazy(() => import('components/views/company/settings/webhooks'));
const CompanySettingsHighLevel = lazy(
  () => import('components/views/company/settings/integrations/highlevel'),
);
const CompanySettingsHubspot = lazy(
  () => import('components/views/company/settings/integrations/hubspot'),
);
const ReportsPage = lazy(() => import('components/views/reports/reports'));
const NotPostedReport = lazy(() => import('components/views/reports/not-posted'));
const EndContactReport = lazy(() => import('components/views/reports/end-contact'));
const UndeliverableReport = lazy(() => import('components/views/reports/undeliverable'));
const DncReport = lazy(() => import('components/views/reports/dnc'));
const TransfersReport = lazy(() => import('components/views/reports/transfers'));
const PostedReport = lazy(() => import('components/views/reports/posted'));

const Campaign = lazy(() => import('components/views/campaign'));
const CampaignDetails = lazy(() => import('./components/views/campaign/details'));
const CampaignRedirect = lazy(() => import('components/views/campaign/CampaignRedirect'));
const CampaignList = lazy(() => import('components/views/home/campaign-list'));
const CampaignsTabs = lazy(() => import('components/views/home/campaigns-tabs'));
const DeprovisionCampaigns = lazy(() => import('components/views/home/deprovision-campaigns'));
const HomeRecordings = lazy(() => import('components/views/home/recordings'));
const CompanyRecordings = lazy(() => import('components/views/company/recordings'));
const LlmAgents = lazy(() => import('components/views/home/llm-agents'));
const LegacyAgentsTab = lazy(() => import('components/views/home/legacy-agents-tab'));
const NewAgentsTab = lazy(() => import('components/views/home/new-agents-tab'));
const AgentBuilderPage = lazy(() => import('./agent-builder/AgentBuilderPage'));
const ExternalCampaignPage = lazy(() => import('components/views/campaign/ExternalCampaignPage'));
const ExternalCampaignDetails = lazy(
  () => import('components/views/campaign/ExternalCampaignDetails'),
);
const CampaignSettings = lazy(
  () => import('components/views/campaign/settings/ExternalCampaignSettings'),
);
const CampaignChatConversation = lazy(
  () => import('components/views/company/chat-conversation/chat-conversation'),
);
const CampaignExperiments = lazy(() => import('components/views/campaign/experiments'));
const ExternalVendors = lazy(() => import('components/views/campaign/vendors/external-vendors'));

const CallCenter = lazy(() => import('components/views/call-center'));
const CallCenterDetails = lazy(() => import('components/views/call-center/details'));

const Home = lazy(() => import('./components/views/home'));
const DashboardPage = lazy(() => import('components/views/dashboard/dashboard'));
const PageNotFound = lazy(() => import('./components/views/page-not-found'));
const SettingsPage = lazy(() => import('components/views/settings/settings'));
const GoHighLevelIntegration = lazy(
  () => import('components/views/settings/integrations/highlevel/HighLevelIntegration'),
);
const LeadIngestionSection = lazy(
  () => import('components/views/settings/integrations/lead-ingestion'),
);
const AppointmentSchedulingSection = lazy(
  () => import('components/views/settings/integrations/appointment-scheduling'),
);
const HubspotIntegration = lazy(
  () => import('components/views/settings/integrations/hubspot/hubspotIntegration'),
);

const Organization = lazy(() => import('components/views/settings/organization/organization'));
const Webhooks = lazy(() => import('components/views/settings/webhooks/webhooks'));
const Integrations = lazy(() => import('components/views/settings/integrations/integrations'));

const ExternalOutreachTemplates = lazy(
  () => import('components/views/campaign/external-outreach-templates/external-outreach-templates'),
);

const LlmConfigurationsPage = lazy(
  () => import('./components/views/company/llm-configurations/llm-configurations-page'),
);
const LlmConfigurations = lazy(
  () => import('./components/views/company/llm-configurations/shared-configs-tab'),
);
const GuardrailsConfigurations = lazy(
  () => import('./components/views/company/llm-configurations/guardrails-configurations'),
);

const GuardrailConfiguration = lazy(
  () =>
    import(
      './components/views/company/llm-configurations/guardrail-configuration/guardrail-configuration'
    ),
);

const LlmConfiguration = lazy(
  () => import('./components/views/company/outreach-template/llm-configuration'),
);
const LlmConfigurationHistoryPage = lazy(
  () => import('./components/views/company/outreach-template/llm-configuration/history-page'),
);

const CompanyConversations = lazy(
  () => import('components/views/company/conversations/conversations'),
);
const ConversationDashboard = lazy(
  () => import('components/views/company/conversations/conversation-dashboard'),
);
const Leads = lazy(() => import('components/views/company/leads-dashboard'));
const LeadLists = lazy(() => import('components/views/company/leads-dashboard/leads-dashboard'));
const AuditTrail = lazy(() => import('components/views/company/leads-dashboard/audit-trail'));
const Analytics = lazy(() => import('components/views/analytics'));
const PostingDashboard = lazy(
  () => import('components/views/company/leads-dashboard/posting-dashboard'),
);

const LeadsDashboardLayout = lazy(() => import('components/views/company/leads-dashboard/layout'));

const Sus: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<div />}>{children}</Suspense>
);
const SusPage: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<PageLoading />}>{children}</Suspense>
);

export const Router: FC = () => {
  const originalUrl = useOriginalLocation();
  const { isInternal } = useAuthContext();
  const canAccessIntegrations = useHasAuthGroup(CURRENT_AVAILABLE_GROUPS.CustomerAdmin);
  const isCallCenterRep = useHasAuthGroup(CURRENT_AVAILABLE_GROUPS.CustomerCallCenterRep);

  const home = (
    <Route
      path={routes.home.path}
      element={
        <SusPage>
          <Home />
        </SusPage>
      }
    >
      <Route
        index
        element={
          <Sus>
            <DashboardPage />
          </Sus>
        }
      />
      <Route
        path={routes.home.campaigns.path}
        element={
          <Sus>
            <CampaignsTabs />
          </Sus>
        }
      >
        <Route index element={<Navigate to={routes.home.campaigns.all.path} replace />} />
        <Route
          path={routes.home.campaigns.all.path}
          element={
            <Sus>
              <CampaignList />
            </Sus>
          }
        />
        <Route
          path={routes.home.campaigns.deprovision.path}
          element={
            <Sus>
              <DeprovisionCampaigns />
            </Sus>
          }
        />
      </Route>
      <Route
        path={routes.home.companies.path}
        element={
          <Sus>
            <CompaniesList />
          </Sus>
        }
      />
      <Route
        path={routes.home.agents.path}
        element={
          <Sus>
            <LlmAgents />
          </Sus>
        }
      >
        <Route index element={<Navigate to={routes.home.agents.new.path} replace />} />
        <Route
          path={routes.home.agents.legacy.path}
          element={
            <Sus>
              <LegacyAgentsTab />
            </Sus>
          }
        />
        <Route
          path={routes.home.agents.new.path}
          element={
            <Sus>
              <NewAgentsTab />
            </Sus>
          }
        />
      </Route>
      <Route
        path={routes.home.agents.builder.path + '/*'}
        element={
          <SusPage>
            <AgentBuilderPage />
          </SusPage>
        }
      />
      <Route
        path={routes.home.recordings.path}
        element={
          <Sus>
            <HomeRecordings />
          </Sus>
        }
      />
      <Route
        path={routes.analytics.path}
        element={
          <Sus>
            <Analytics />
          </Sus>
        }
      />
    </Route>
  );
  const loginCallback = <Route path={routes.login.callback.path} element={<Navigate to="/" />} />;
  const alreadyLoggedIn = (
    <Route
      path={routes.login.path}
      element={<Navigate to={originalUrl ? originalUrl : routes.home.path} />}
    />
  );
  const notFound = <Route path="*" element={<PageNotFound />} />;

  if (!isInternal && isCallCenterRep) {
    return (
      <Suspense fallback={<div />}>
        <Routes>
          <Route
            path={routes.home.path}
            element={
              <SusPage>
                <Home />
              </SusPage>
            }
          >
            <Route
              index
              element={
                <Sus>
                  <CompanyConversations />
                </Sus>
              }
            />
          </Route>
          {alreadyLoggedIn}
          {loginCallback}
          {notFound}
        </Routes>
      </Suspense>
    );
  }

  if (!isInternal) {
    return (
      <Suspense fallback={<div />}>
        <Routes>
          <Route
            path={routes.home.path}
            element={
              <SusPage>
                <Home />
              </SusPage>
            }
          >
            <Route
              index
              element={
                <Sus>
                  <DashboardPage />
                </Sus>
              }
            />
            <Route
              path={routes.home.campaigns.path}
              element={
                <Sus>
                  <CampaignList />
                </Sus>
              }
            ></Route>

            <Route
              path={routes.campaign.path}
              element={
                <SusPage>
                  <ExternalCampaignPage />
                </SusPage>
              }
            >
              <Route
                index
                element={
                  <SusPage>
                    <ExternalCampaignDetails />
                  </SusPage>
                }
              />
              <Route
                path={routes.company.campaign.conversation.path}
                element={
                  <Sus>
                    <ConversationPage />
                  </Sus>
                }
              />
              <Route
                path={routes.campaign.outreachTemplates.path}
                element={
                  <Sus>
                    <ExternalOutreachTemplates />
                  </Sus>
                }
              />
              <Route
                path={routes.campaign.experiments.path}
                element={
                  <Sus>
                    <CampaignExperiments />
                  </Sus>
                }
              />
              <Route
                path={routes.campaign.analytics.path}
                element={
                  <Sus>
                    <Analytics />
                  </Sus>
                }
              />
              <Route
                path={routes.campaign.ivrs.path}
                element={
                  <Sus>
                    <ExternalVendors />
                  </Sus>
                }
              />
              {canAccessIntegrations && (
                <Route
                  path={routes.company.campaign.settings.path}
                  element={
                    <Sus>
                      <CampaignSettings />
                    </Sus>
                  }
                />
              )}
            </Route>
            <Route
              path={routes.analytics.path}
              element={
                <Sus>
                  <Analytics />
                </Sus>
              }
            />
            <Route
              path={routes.reports.path}
              element={
                <Sus>
                  <ReportsPage />
                </Sus>
              }
            >
              <Route
                path={routes.reports.endContact.path}
                element={
                  <Sus>
                    <EndContactReport />
                  </Sus>
                }
              />
              <Route
                path={routes.reports.notPosted.path}
                element={
                  <Sus>
                    <NotPostedReport />
                  </Sus>
                }
              />
              <Route
                path={routes.reports.undeliverable.path}
                element={
                  <Sus>
                    <UndeliverableReport />
                  </Sus>
                }
              />
              <Route
                path={routes.reports.dnc.path}
                element={
                  <Sus>
                    <DncReport />
                  </Sus>
                }
              />
              <Route
                path={routes.reports.transfers.path}
                element={
                  <Sus>
                    <TransfersReport />
                  </Sus>
                }
              />
              <Route
                path={routes.reports.posted.path}
                element={
                  <Sus>
                    <PostedReport />
                  </Sus>
                }
              />
            </Route>

            <Route
              path={routes.leads.path}
              element={
                <Sus>
                  <LeadLists />
                </Sus>
              }
            />

            <Route
              path={routes.conversationFeed.path}
              element={
                <Sus>
                  <ConversationDashboard />
                </Sus>
              }
            />

            <Route
              path={routes.ivrs.path}
              element={
                <Sus>
                  <ExternalIvrs />
                </Sus>
              }
            />

            <Route
              path={routes.leads.path}
              element={
                <Sus>
                  <LeadsDashboardLayout />
                </Sus>
              }
            >
              <Route
                index
                element={
                  <Sus>
                    <LeadLists />
                  </Sus>
                }
              />
              <Route
                path={routes.leads.lists.path}
                element={
                  <Sus>
                    <LeadLists />
                  </Sus>
                }
              />
              <Route
                path={routes.leads.imports.path}
                element={
                  <Sus>
                    <LeadListsView />
                  </Sus>
                }
              />
              <Route
                path={routes.leads.pending.path}
                element={
                  <Sus>
                    <LeadListDashboard />
                  </Sus>
                }
              />
              <Route
                path={routes.leads.importCreate.path + '/*'}
                element={
                  <Sus>
                    <LeadListCreateViewWrapper />
                  </Sus>
                }
              />
              <Route
                path={routes.leads.importDetail.path}
                element={
                  <Sus>
                    <LeadListDetails />
                  </Sus>
                }
              />
              <Route
                path={routes.leads.auditTrail.path}
                element={
                  <Sus>
                    <AuditTrail />
                  </Sus>
                }
              />
            </Route>

            <Route
              path={routes.settings.path}
              element={
                <SusPage>
                  <SettingsPage />
                </SusPage>
              }
            >
              <Route
                path={routes.settings.organization.path}
                element={
                  <Sus>
                    <Organization />
                  </Sus>
                }
              />
              <Route
                path={routes.settings.webhooks.path}
                element={
                  <Sus>
                    <Webhooks />
                  </Sus>
                }
              />
              {canAccessIntegrations && (
                <Route
                  path={routes.settings.integrations.path}
                  element={
                    <SusPage>
                      <Integrations />
                    </SusPage>
                  }
                >
                  <Route
                    path={routes.settings.integrations.oldHighLevel.path}
                    element={
                      <Sus>
                        <GoHighLevelIntegration />
                      </Sus>
                    }
                  />
                  <Route
                    path={routes.settings.integrations.goHighLevel.path}
                    element={
                      <Sus>
                        <GoHighLevelIntegration />
                      </Sus>
                    }
                  />
                  <Route
                    path={routes.settings.integrations.hubspot.path}
                    element={
                      <Sus>
                        <HubspotIntegration />
                      </Sus>
                    }
                  />
                  <Route
                    path={routes.settings.integrations.leadIngestion.path}
                    element={
                      <Sus>
                        <LeadIngestionSection />
                      </Sus>
                    }
                  />
                  <Route
                    path={routes.settings.integrations.appointmentScheduling.path}
                    element={
                      <Sus>
                        <AppointmentSchedulingSection />
                      </Sus>
                    }
                  />
                </Route>
              )}
            </Route>
            <Route
              path={routes.company.conversations.path}
              element={
                <Sus>
                  <CompanyConversations />
                </Sus>
              }
            />
          </Route>
          {alreadyLoggedIn}
          {loginCallback}
          {notFound}
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<div />}>
      <Routes>
        {home}
        <Route
          path={routes.company.path}
          element={
            <SusPage>
              <Company />
            </SusPage>
          }
        >
          <Route
            index
            element={
              <Sus>
                <CompanyDetails />
              </Sus>
            }
          />
          <Route
            path={routes.company.campaigns.path}
            element={
              <Sus>
                <CompanyCampaigns />
              </Sus>
            }
          />
          <Route
            path={routes.company.callCenters.path}
            element={
              <Sus>
                <CompanyCallCenters />
              </Sus>
            }
          />
          <Route
            path={routes.company.nlpAgents.path}
            element={
              <Sus>
                <CompanyNlpAgents />
              </Sus>
            }
          />
          <Route
            path={routes.company.virtualAgents.path}
            element={
              <Sus>
                <CompanyVirtualAgents />
              </Sus>
            }
          />
          <Route
            path={routes.company.outreachTemplate.path}
            element={
              <Sus>
                <CompanyOutreachTemplate />
              </Sus>
            }
          />
          <Route
            path={routes.company.outreachTemplates.path}
            element={
              <Sus>
                <CompanyOutreachTemplates />
              </Sus>
            }
          />
          <Route
            path={routes.company.outreachTemplate.llmConfiguration.path}
            element={
              <Sus>
                <LlmConfiguration />
              </Sus>
            }
          >
            <Route
              path={routes.company.agentConfiguration.history.path}
              element={
                <Sus>
                  <LlmConfigurationHistoryPage />
                </Sus>
              }
            />
          </Route>
          <Route
            path={routes.company.agentConfiguration.path}
            element={
              <Sus>
                <LlmConfiguration />
              </Sus>
            }
          >
            <Route
              path={routes.company.agentConfiguration.history.path}
              element={
                <Sus>
                  <LlmConfigurationHistoryPage />
                </Sus>
              }
            />
          </Route>
          <Route
            path={routes.company.configurations.path}
            element={
              <Sus>
                <LlmConfigurationsPage />
              </Sus>
            }
          >
            <Route
              index
              element={
                <Navigate to={routes.company.configurations.llmConfigurations.path} replace />
              }
            />

            <Route
              path={routes.company.configurations.llmConfigurations.path}
              element={
                <Sus>
                  <LlmConfigurations />
                </Sus>
              }
            />
            <Route
              path={routes.company.configurations.guardrails.path}
              element={
                <Sus>
                  <GuardrailsConfigurations />
                </Sus>
              }
            />
            <Route
              path={routes.company.configurations.guardrail.path}
              element={
                <Sus>
                  <GuardrailConfiguration />
                </Sus>
              }
            />
          </Route>
          <Route
            path={routes.company.brands.path}
            element={
              <Sus>
                <CompanyBrands />
              </Sus>
            }
          />
          <Route
            path={routes.company.scripts.path}
            element={
              <Sus>
                <CompanyScripts />
              </Sus>
            }
          />
          <Route
            path={routes.company.users.path}
            element={
              <Sus>
                <UserCompanies />
              </Sus>
            }
          />
          <Route
            path={routes.company.ivrs.path}
            element={
              <Sus>
                <CompanyIvrs />
              </Sus>
            }
          />
          <Route
            path={routes.company.recordings.path}
            element={
              <Sus>
                <CompanyRecordings />
              </Sus>
            }
          />
          <Route
            path={routes.company.userPool.path}
            element={
              <Sus>
                <CompanyUserPoolPage />
              </Sus>
            }
          />
          <Route
            path={routes.company.settings.path}
            element={
              <SusPage>
                <CompanySettingsPage />
              </SusPage>
            }
          >
            <Route
              index
              element={<Navigate to={routes.company.settings.organization.path} replace />}
            />
            <Route
              path={routes.company.settings.organization.path}
              element={
                <Sus>
                  <CompanySettingsOrganization />
                </Sus>
              }
            />
            <Route
              path={routes.company.settings.webhooks.path}
              element={
                <Sus>
                  <CompanySettingsWebhooks />
                </Sus>
              }
            />
            <Route
              path={routes.company.settings.integrations.path}
              element={
                <SusPage>
                  <Integrations />
                </SusPage>
              }
            >
              <Route
                index
                element={
                  <Navigate to={routes.company.settings.integrations.leadIngestion.path} replace />
                }
              />
              <Route
                path={routes.company.settings.integrations.goHighLevel.path}
                element={
                  <Sus>
                    <CompanySettingsHighLevel />
                  </Sus>
                }
              />
              <Route
                path={routes.company.settings.integrations.hubspot.path}
                element={
                  <Sus>
                    <CompanySettingsHubspot />
                  </Sus>
                }
              />
              <Route
                path={routes.company.settings.integrations.leadIngestion.path}
                element={
                  <Sus>
                    <LeadIngestionSection />
                  </Sus>
                }
              />
              <Route
                path={routes.company.settings.integrations.appointmentScheduling.path}
                element={
                  <Sus>
                    <AppointmentSchedulingSection />
                  </Sus>
                }
              />
            </Route>
          </Route>
          <Route
            path={routes.company.reports.path}
            element={
              <Sus>
                <ReportsPage />
              </Sus>
            }
          >
            <Route
              path={routes.company.reports.endContact.path}
              element={
                <Sus>
                  <EndContactReport />
                </Sus>
              }
            />
            <Route
              path={routes.company.reports.notPosted.path}
              element={
                <Sus>
                  <NotPostedReport />
                </Sus>
              }
            />
            <Route
              path={routes.company.reports.undeliverable.path}
              element={
                <Sus>
                  <UndeliverableReport />
                </Sus>
              }
            />
            <Route
              path={routes.company.reports.dnc.path}
              element={
                <Sus>
                  <DncReport />
                </Sus>
              }
            />

            <Route
              path={routes.company.reports.transfers.path}
              element={
                <Sus>
                  <TransfersReport />
                </Sus>
              }
            />
            <Route
              path={routes.company.reports.posted.path}
              element={
                <Sus>
                  <PostedReport />
                </Sus>
              }
            />
          </Route>
          <Route
            path={routes.company.analytics.path}
            element={
              <Sus>
                <Analytics />
              </Sus>
            }
          />

          <Route
            path={routes.company.campaign.path}
            element={
              <SusPage>
                <Campaign />
              </SusPage>
            }
          >
            <Route
              index
              element={
                <Sus>
                  <CampaignDetails />
                </Sus>
              }
            />
            <Route
              path={routes.company.campaign.conversation.path}
              element={
                <Sus>
                  <ConversationPage />
                </Sus>
              }
            />
            <Route
              path={routes.company.campaign.callCenter.path}
              element={
                <Sus>
                  <CampaignCallCenter />
                </Sus>
              }
            />
            <Route
              path={routes.company.campaign.leadSource.path}
              element={
                <Sus>
                  <CampaignLeadSource />
                </Sus>
              }
            />
            <Route
              path={routes.company.campaign.leadList.path}
              element={
                <Sus>
                  <CampaignLeadList />
                </Sus>
              }
            />
            <Route
              path={routes.company.campaign.leadLists.path}
              element={
                <Sus>
                  <CampaignLeadLists />
                </Sus>
              }
            />
            <Route
              path={routes.company.campaign.outreachTemplate.path}
              element={
                <Sus>
                  <CampaignOutreachTemplate />
                </Sus>
              }
            />
            <Route
              path={routes.company.campaign.vendors.path}
              element={
                <Sus>
                  <Vendors />
                </Sus>
              }
            />
            <Route
              path={routes.company.campaign.leadDelivery.path}
              element={
                <Sus>
                  <CampaignPosting />
                </Sus>
              }
            />
            <Route
              path={routes.company.campaign.agentPhones.path}
              element={
                <Sus>
                  <CampaignAgentPhones />
                </Sus>
              }
            />
            <Route
              path={routes.company.campaign.dnc.path}
              element={
                <Sus>
                  <CampaignDnc />
                </Sus>
              }
            />
            <Route
              path={routes.company.campaign.testLeads.path}
              element={
                <Sus>
                  <CampaignTestLeads />
                </Sus>
              }
            />
            <Route
              path={routes.company.campaign.chatConversation.path}
              element={
                <Sus>
                  <CampaignChatConversation />
                </Sus>
              }
            />
            <Route
              path={routes.company.campaign.experiments.path}
              element={
                <Sus>
                  <CampaignExperiments />
                </Sus>
              }
            />
            <Route
              path={routes.company.campaign.analytics.path}
              element={
                <Sus>
                  <Analytics />
                </Sus>
              }
            />
            <Route path="*" element={<Navigate to="" />} />
          </Route>

          <Route
            path={routes.company.callCenter.path}
            element={
              <SusPage>
                <CallCenter />
              </SusPage>
            }
          >
            <Route
              path={routes.company.callCenter.path}
              element={
                <Sus>
                  <CallCenterDetails />
                </Sus>
              }
            />
          </Route>

          <Route
            path={routes.company.conversations.path}
            element={
              <Sus>
                <CompanyConversations />
              </Sus>
            }
          />

          <Route
            path={routes.company.conversationDashboard.path}
            element={
              <Sus>
                <ConversationDashboard />
              </Sus>
            }
          />

          <Route
            path={routes.company.leads.path}
            element={
              <Sus>
                <Leads />
              </Sus>
            }
          >
            <Route index element={<Navigate to={routes.company.leads.lists.path} replace />} />

            <Route
              path={routes.company.leads.lists.path}
              element={
                <Sus>
                  <LeadLists />
                </Sus>
              }
            />
            <Route
              path={routes.company.leads.posting.path}
              element={
                <Sus>
                  <PostingDashboard />
                </Sus>
              }
            />
            <Route
              path={routes.company.leads.imports.path}
              element={
                <Sus>
                  <LeadListsView />
                </Sus>
              }
            />
            <Route
              path={routes.company.leads.pending.path}
              element={
                <Sus>
                  <LeadListDashboard />
                </Sus>
              }
            />
            <Route
              path={routes.company.leads.importCreate.path + '/*'}
              element={
                <Sus>
                  <LeadListCreateViewWrapper />
                </Sus>
              }
            />
            <Route
              path={routes.company.leads.importDetail.path}
              element={
                <Sus>
                  <LeadListDetails />
                </Sus>
              }
            />
            <Route
              path={routes.company.leads.auditTrail.path}
              element={
                <Sus>
                  <AuditTrail />
                </Sus>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="" />} />
        </Route>
        <Route
          path={routes.campaign.path}
          element={
            <SusPage>
              <CampaignRedirect />
            </SusPage>
          }
        />
        {alreadyLoggedIn}
        {loginCallback}
        {notFound}
      </Routes>
    </Suspense>
  );
};

export const AuthRouter: FC = () => {
  const originalUrl = useOriginalLocation();

  return (
    <Routes>
      <Route path={routes.login.path} element={<Login originalUrl={originalUrl} />} />
      <Route path={routes.login.callback.path} element={<LoginCallback />} />
      <Route path="*" element={<Navigate to={routes.login.path} />} />
    </Routes>
  );
};
